import React, { useEffect, ReactNode, useState, useCallback, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';
import useFirebaseAuth from '../use/useFirebaseAuth';
import { loadSpace, InitOptions } from '@usersnap/browser';
import { collection, getDocs, doc, runTransaction, arrayRemove, arrayUnion } from 'firebase/firestore';
import { firestore } from '../services/startFirestore';
import debounce from 'lodash/debounce';
import { usePostHog } from 'posthog-js/react';

import {
    usersnapSpaceId,
    environmentType,
    firebaseConfig,
} from '../config';
import { createDocumentStore, type DocumentStore } from '../store';
import { useSet } from '../use/data/useSet';
import { Notification, UserSettings } from '#types/System.types';
import { SecurityGroup, BusinessGroup } from '#types/System.types';
import { useList } from '../use/data/useList';
import { UserContext, UserContextType, NotificationReadState, UserProfileData } from './UserContext';
import { User as SystemUser } from '#types/System.types';
import { clearEntityRecordsRegistry } from '@/store/entityRecordsRegistry/store';
import { clearAllEntityRecordStores } from '@/store/entityRecord/entityRecordStore';
import { formStore } from '@/store/form/formStore';
import { useNotificationBundles } from '../use/useNotificationBundles';
import { NotificationBundles } from '../services/NotificationBundles';
import { useNotificationToasts } from '../use/useNotificationToasts';

const inactiveUserStore = createDocumentStore<SystemUser>('users/__inactive__');

interface UserProviderProps { children: ReactNode; }


/**
 * Provider component that manages user authentication state and related functionality.
 * Handles user settings, notifications, form subscriptions, and analytics integration.
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const { set } = useSet();
    const { user, logout, loading } = useFirebaseAuth();
    const posthog = usePostHog();

    const userStore = useMemo<DocumentStore<SystemUser>>(
        () => user?.uid ? createDocumentStore<SystemUser>(`users/${user.uid}`) : inactiveUserStore,
        [user?.uid],
    );

    useEffect(() => {
        if (!user) return;
        userStore.connect();
        return () => userStore.disconnect();
    }, [user, userStore]);

    const previousUidRef = useRef<string | null>(user?.uid ?? null);
    useEffect(() => {
        const previousUid = previousUidRef.current;
        const currentUid = user?.uid ?? null;
        if (previousUid !== null && previousUid !== currentUid) {
            formStore.reset();
            clearAllEntityRecordStores();
            clearEntityRecordsRegistry();
        }
        previousUidRef.current = currentUid;
    }, [user?.uid]);

    const userSnap = useSnapshot(userStore.state);
    const userDocData = user ? (userSnap.data as SystemUser | null) : null;
    const userDocLoading = user ? userSnap.loading : false;

    const notificationBundles = useNotificationBundles({
        userId: user?.uid,
        enabled: !!user,
    });
    const notificationBundleDocIdByEventId = notificationBundles.notificationBundleDocIdByEventId;
    const notificationEntryByEventId = notificationBundles.notificationEntryByEventId;

    useNotificationToasts({ unreadNotifications: notificationBundles.unreadNotifications });

    // Extract settings from user document for backwards compatibility
    const settings = useMemo(() => ({
        data: userDocData ? {
            preferDarkMode: userDocData.settings?.preferDarkMode,
            fontSize: userDocData.settings?.fontSize,
            role: userDocData.settings?.role,
            currentOrganization: userDocData.settings?.currentOrganization,
            homePageViewId: userDocData.settings?.homePageViewId,
            filterPreferences: userDocData.settings?.filterPreferences,
        } as UserSettings : null,
    }), [userDocData]);

    // Extract user profile data (name, email, phone)
    const userProfile = useMemo((): UserProfileData | null => {
        if (!userDocData) return null;
        return {
            name: userDocData.name,
            email: userDocData.email,
            phone: userDocData.phone,
        };
    }, [userDocData]);

    useEffect(() => {
        if (!user || !userDocData || userDocLoading) return;

        const updates: Partial<SystemUser> = {};

        if (user.email && userDocData.email !== user.email) {
            updates.email = user.email;
        }

        if (!userDocData.name && user.displayName) {
            updates.name = user.displayName;
        }

        if (Object.keys(updates).length > 0) {
            void userStore.setMerge(updates, { debounce: true });
        }
    }, [user, userDocData, userDocLoading, userStore]);

    const [userRights, setUserRights] = useState<{ [rightId: string]: boolean }>({});
    const [userSecurityGroups, setUserSecurityGroups] = useState<string[]>([]);
    const [userBusinessGroups, setUserBusinessGroups] = useState<string[]>([]);
    const [securityGroups, setSecurityGroups] = useState<SecurityGroup[]>([]);
    const [businessGroups, setBusinessGroups] = useState<BusinessGroup[]>([]);

    const users = useList({
        path: 'users',
        enabled: !!user && !loading,
        transformDocs: (docs) => docs.map(doc => ({ docId: doc.id, ...doc.data() }))
    });

    const hasEntityAccess = useMemo(() => {
        if (Object.keys(userRights).length === 0) {
            return false;
        }
        
        return !!(
            userRights['recordsRead'] ||
            userRights['recordsWrite'] ||
            userRights['recordsAdmin'] ||
            userRights['orgsAdmin']
        );
    }, [userRights]);

    const stableUsers = useMemo(() => users.docs ?? [], [users.docs]);

    useEffect(() => {
        const fetchSecurityGroups = async () => {
            if (!user?.uid) return;

            const groupsSnap = await getDocs(collection(firestore, 'securityGroups'));
            setSecurityGroups(groupsSnap.docs.map(groupDoc => ({
                ...(groupDoc.data() as SecurityGroup),
                docId: groupDoc.id,
            })));
        };

        void fetchSecurityGroups();
    }, [user?.uid]);

    useEffect(() => {
        const fetchBusinessGroups = async () => {
            if (!user?.uid) return;

            const groupsSnap = await getDocs(collection(firestore, 'businessGroups'));
            setBusinessGroups(groupsSnap.docs.map(groupDoc => ({
                ...(groupDoc.data() as BusinessGroup),
                docId: groupDoc.id,
            })));
        };

        void fetchBusinessGroups();
    }, [user?.uid]);

    useEffect(() => {
        const rightsMap = (userDocData?.userRights ?? {}) as Record<string, unknown>;
        const nextUserRights: { [rightId: string]: boolean } = {};
        Object.entries(rightsMap).forEach(([rightId, value]) => {
            if (value === true) {
                nextUserRights[rightId] = true;
            }
        });
        setUserRights(nextUserRights);
    }, [userDocData?.userRights]);

    useEffect(() => {
        const groupsArray = userDocData?.securityGroups ?? [];
        const validGroups = (Array.isArray(groupsArray) ? groupsArray : []).filter((g): g is string => typeof g === 'string');
        setUserSecurityGroups(validGroups);
        formStore.state.userGroups = validGroups;
    }, [userDocData?.securityGroups]);

    useEffect(() => {
        const groupsArray = userDocData?.businessGroups ?? [];
        setUserBusinessGroups(
            (Array.isArray(groupsArray) ? groupsArray : []).filter((g): g is string => typeof g === 'string'),
        );
    }, [userDocData?.businessGroups]);

    useEffect(() => {
        if (user && user.email) {

            if (posthog) {
                posthog.identify(user.uid, {
                    email: user.email,
                    displayName: user.displayName,
                });
                posthog.capture('user_logged_in', {
                    userId: user.uid,
                    email: user.email,
                    loginMethod: 'firebase',
                });
            }

            loadSpace(usersnapSpaceId).then((api) => {
                api.init({
                    user: {
                        email: user?.email,
                        userId: user?.uid,
                    },
                    custom: {
                        displayName: user?.displayName,
                        userId: user?.uid,
                        email: user?.email,
                        firebaseProjectId: firebaseConfig.projectId,
                        environmentType,
                    },
                } as InitOptions);
                console.log('Usersnap API initialized');
            }).catch((error) => {
                console.error('Failed to initialize Usersnap API', error);
            });
        }
    }, [user, posthog]);

    /**
     * Debounced function to update user settings in the database
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdateSettings = useCallback(
        debounce(async (newSettings: UserSettings) => {
            if (user) {
                await set('users', user.uid, { settings: newSettings });
            }
        }, 1500),
        [user, set]
    );

    /**
     * Updates user settings and triggers debounced save to database
     */
    const updateSettings = useCallback((newSettings: UserSettings) => {
        debouncedUpdateSettings(newSettings);
    }, [debouncedUpdateSettings]);

    /**
     * Toggles the read status of a notification in Firestore
     */
    const toggleNotificationRead = useCallback(async (notification: Notification) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!notification?.notificationEvent?.docId) {
            throw new Error('Invalid notification data');
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const userRef = doc(firestore, 'users', user.uid);
                const userDocSnapshot = await transaction.get(userRef);

                if (!userDocSnapshot.exists()) {
                    throw new Error('User document not found');
                }

                const userData = userDocSnapshot.data();
                const notificationEventId = notification.notificationEvent.docId;
                if (!notificationEventId) {
                    throw new Error('Invalid notification data');
                }
                const currentEntry = notificationEntryByEventId.get(notificationEventId);
                const bundleDocId = notificationBundleDocIdByEventId.get(notificationEventId);
                const nextStatus = currentEntry?.status === 'read' ? NotificationReadState.UNREAD : NotificationReadState.READ;

                if (bundleDocId) {
                    await NotificationBundles.setEntryStatus({
                        transaction,
                        userId: user.uid,
                        userDocData: userData,
                        bundleDocId,
                        notification,
                        status: nextStatus === NotificationReadState.READ ? 'read' : 'unread',
                    });
                    return;
                }

                await NotificationBundles.appendEntry({
                    transaction,
                    userId: user.uid,
                    userDocData: userData,
                    entry: {
                        notification,
                        status: nextStatus === NotificationReadState.READ ? 'read' : 'unread',
                    },
                });
            });
        } catch (error) {
            console.error('Failed to toggle notification:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to toggle notification: ${error.message}`);
            }
            throw new Error('Failed to toggle notification: Unknown error occurred');
        }
    }, [user, notificationBundleDocIdByEventId, notificationEntryByEventId]);

    const deleteNotification = useCallback(async (notification: Notification) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!notification?.notificationEvent?.docId) {
            throw new Error('Invalid notification data');
        }

        const notificationEventId = notification.notificationEvent.docId;
        const bundleDocId = notificationBundleDocIdByEventId.get(notificationEventId);
        if (!bundleDocId) {
            return;
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const userRef = doc(firestore, 'users', user.uid);
                const userDocSnapshot = await transaction.get(userRef);

                if (!userDocSnapshot.exists()) {
                    throw new Error('User document not found');
                }

                await NotificationBundles.removeEntry({
                    transaction,
                    userId: user.uid,
                    userDocData: userDocSnapshot.data(),
                    bundleDocId,
                    notificationEventId,
                });
            });
        } catch (error) {
            console.error('Failed to delete notification:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to delete notification: ${error.message}`);
            }
            throw new Error('Failed to delete notification: Unknown error occurred');
        }
    }, [user, notificationBundleDocIdByEventId]);

    const markAllNotificationsRead = useCallback(async () => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        const entries = notificationBundles.entries.filter((entry) => entry.status === 'unread');
        if (entries.length === 0) return;

        try {
            await runTransaction(firestore, async (transaction) => {
                const userRef = doc(firestore, 'users', user.uid);
                const userDocSnapshot = await transaction.get(userRef);

                if (!userDocSnapshot.exists()) {
                    throw new Error('User document not found');
                }

                await NotificationBundles.setAllEntriesStatus({
                    transaction,
                    userId: user.uid,
                    userDocData: userDocSnapshot.data(),
                    entries,
                    bundleDocIdByEventId: notificationBundleDocIdByEventId,
                    status: 'read',
                });
            });
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to mark all as read: ${error.message}`);
            }
            throw new Error('Failed to mark all as read: Unknown error occurred');
        }
    }, [user, notificationBundles.entries, notificationBundleDocIdByEventId]);

    const markAllNotificationsUnread = useCallback(async () => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        const entries = notificationBundles.entries.filter((entry) => entry.status === 'read');
        if (entries.length === 0) return;

        try {
            await runTransaction(firestore, async (transaction) => {
                const userRef = doc(firestore, 'users', user.uid);
                const userDocSnapshot = await transaction.get(userRef);

                if (!userDocSnapshot.exists()) {
                    throw new Error('User document not found');
                }

                await NotificationBundles.setAllEntriesStatus({
                    transaction,
                    userId: user.uid,
                    userDocData: userDocSnapshot.data(),
                    entries,
                    bundleDocIdByEventId: notificationBundleDocIdByEventId,
                    status: 'unread',
                });
            });
        } catch (error) {
            console.error('Failed to mark all notifications as unread:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to mark all as unread: ${error.message}`);
            }
            throw new Error('Failed to mark all as unread: Unknown error occurred');
        }
    }, [user, notificationBundles.entries, notificationBundleDocIdByEventId]);

    const deleteAllNotifications = useCallback(async () => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        const entries = notificationBundles.entries;
        if (entries.length === 0) return;

        try {
            await runTransaction(firestore, async (transaction) => {
                const userRef = doc(firestore, 'users', user.uid);
                const userDocSnapshot = await transaction.get(userRef);

                if (!userDocSnapshot.exists()) {
                    throw new Error('User document not found');
                }

                await NotificationBundles.removeAllEntries({
                    transaction,
                    userId: user.uid,
                    userDocData: userDocSnapshot.data(),
                    entries,
                    bundleDocIdByEventId: notificationBundleDocIdByEventId,
                });
            });
        } catch (error) {
            console.error('Failed to delete all notifications:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to delete all: ${error.message}`);
            }
            throw new Error('Failed to delete all: Unknown error occurred');
        }
    }, [user, notificationBundles.entries, notificationBundleDocIdByEventId]);

    /**
     * Subscribes the current user to notifications for a specific form
     */
    const subscribeToForm = useCallback(async (formId: string) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!formId) {
            throw new Error('Form ID is required');
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const formRef = doc(firestore, 'forms', formId);
                const formDoc = await transaction.get(formRef);

                if (!formDoc.exists()) {
                    throw new Error(`Form not found: ${formId}`);
                }

                transaction.update(formRef, {
                    notificationsSubscribers: arrayUnion(user.uid)
                });
            });
        } catch (error) {
            console.error('Failed to subscribe to form:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to subscribe to form: ${error.message}`);
            }
            throw new Error('Failed to subscribe to form: Unknown error occurred');
        }
    }, [user]);

    /** Unsubscribe from notifications for a form */
    const unsubscribeFromForm = useCallback(async (formId: string) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!formId) {
            throw new Error('Form ID is required');
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const formRef = doc(firestore, 'forms', formId);
                const formDoc = await transaction.get(formRef);

                if (!formDoc.exists()) {
                    throw new Error(`Form not found: ${formId}`);
                }

                transaction.update(formRef, {
                    notificationsSubscribers: arrayRemove(user.uid)
                });
            });
        } catch (error) {
            console.error('Failed to unsubscribe from form:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to unsubscribe from form: ${error.message}`);
            }
            throw new Error('Failed to unsubscribe from form: Unknown error occurred');
        }
    }, [user]);

    /** Subscribe to notifications for an entity record */
    const subscribeToEntityRecord = useCallback(async (entityId: string, recordId: string) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!entityId || !recordId) {
            throw new Error('Entity ID and Record ID are required');
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const recordRef = doc(firestore, `entities/${entityId}/records`, recordId);
                const recordDoc = await transaction.get(recordRef);

                if (!recordDoc.exists()) {
                    throw new Error(`Entity record not found: ${entityId}/${recordId}`);
                }

                transaction.update(recordRef, {
                    notificationsSubscribers: arrayUnion(user.uid)
                });
            });
        } catch (error) {
            console.error('Failed to subscribe to entity record:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to subscribe to entity record: ${error.message}`);
            }
            throw new Error('Failed to subscribe to entity record: Unknown error occurred');
        }
    }, [user]);

    /** Unsubscribe from notifications for an entity record */
    const unsubscribeFromEntityRecord = useCallback(async (entityId: string, recordId: string) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!entityId || !recordId) {
            throw new Error('Entity ID and Record ID are required');
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const recordRef = doc(firestore, `entities/${entityId}/records`, recordId);
                const recordDoc = await transaction.get(recordRef);

                if (!recordDoc.exists()) {
                    throw new Error(`Entity record not found: ${entityId}/${recordId}`);
                }

                transaction.update(recordRef, {
                    notificationsSubscribers: arrayRemove(user.uid)
                });
            });
        } catch (error) {
            console.error('Failed to unsubscribe from entity record:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to unsubscribe from entity record: ${error.message}`);
            }
            throw new Error('Failed to unsubscribe from entity record: Unknown error occurred');
        }
    }, [user]);

    /** Assign a form to a user or clear assignment */
    const assignFormToUser = useCallback(async (formId: string, userId?: string | null) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!formId) {
            throw new Error('Form ID is required');
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const formRef = doc(firestore, 'forms', formId);
                const formDoc = await transaction.get(formRef);

                if (!formDoc.exists()) {
                    throw new Error(`Form not found: ${formId}`);
                }

                const assigneeId = userId === undefined ? user.uid : userId;

                if (assigneeId !== null) {
                    const userRef = doc(firestore, 'users', assigneeId);
                    const userDoc = await transaction.get(userRef);
                    if (!userDoc.exists()) {
                        console.error(`Target user not found: ${assigneeId}`);
                        throw new Error(`Target user not found: ${assigneeId}`);
                    }
                }

                transaction.update(formRef, {
                    assignedTo: assigneeId,
                    meta: {
                        lastModified: new Date(),
                        userId: user.uid,
                    }
                });
            });
        } catch (error) {
            console.error('Failed to assign form:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to assign form: ${error.message}`);
            }
            throw new Error('Failed to assign form: Unknown error occurred');
        }
    }, [user]);

    /** Update form notification subscribers */
    const updateFormSubscribers = useCallback(async (formId: string, userIds: string[]) => {
        if (!user?.uid) {
            throw new Error('Authentication required');
        }

        if (!formId) {
            throw new Error('Form ID is required');
        }

        try {
            await runTransaction(firestore, async (transaction) => {
                const formRef = doc(firestore, 'forms', formId);
                const formDoc = await transaction.get(formRef);

                if (!formDoc.exists()) {
                    throw new Error(`Form not found: ${formId}`);
                }

                // Validate all user IDs exist
                for (const userId of userIds) {
                    const userRef = doc(firestore, 'users', userId);
                    const userDoc = await transaction.get(userRef);
                    if (!userDoc.exists()) {
                        throw new Error(`User not found: ${userId}`);
                    }
                }

                transaction.update(formRef, {
                    notificationsSubscribers: userIds,
                    meta: {
                        lastModified: new Date(),
                        userId: user.uid,
                    }
                });
            });
        } catch (error) {
            console.error('Failed to update form subscribers:', error);
            if (error instanceof Error) {
                throw new Error(`Failed to update form subscribers: ${error.message}`);
            }
            throw new Error('Failed to update form subscribers: Unknown error occurred');
        }
    }, [user]);

    const wrappedLogout = useCallback(async () => {
        if (posthog && user) {
            posthog.capture('user_logged_out', {
                userId: user.uid,
                email: user.email,
            });
            posthog.reset();
        }
        await logout();
    }, [logout, posthog, user]);

    useEffect(() => {
        if (posthog && !loading && !user) {
            posthog.capture('session_started', {
                source: 'web',
                timestamp: new Date().toISOString(),
            });
        }
    }, [loading, user, posthog]);


    /** Combined context value */
    const userValue: UserContextType = useMemo(() => ({
        user,
        logout: wrappedLogout,
        isLoggedIn: user !== null,
        loading,
        settings: settings.data,
        updateSettings,
        userProfile,
        userProfileLoading: userDocLoading,
        userRights,
        userSecurityGroups,
        userBusinessGroups,
        securityGroups,
        businessGroups,
        hasEntityAccess,
        notificationBundles,
        toggleNotificationRead,
        deleteNotification,
        markAllNotificationsRead,
        markAllNotificationsUnread,
        deleteAllNotifications,
        subscribeToForm,
        unsubscribeFromForm,
        subscribeToEntityRecord,
        unsubscribeFromEntityRecord,
        users: stableUsers,
        assignFormToUser,
        updateFormSubscribers,
    }), [
        user,
        wrappedLogout,
        loading,
        settings.data,
        updateSettings,
        userProfile,
        userDocLoading,
        userRights,
        userSecurityGroups,
        userBusinessGroups,
        securityGroups,
        businessGroups,
        hasEntityAccess,
        notificationBundles,
        toggleNotificationRead,
        deleteNotification,
        markAllNotificationsRead,
        markAllNotificationsUnread,
        deleteAllNotifications,
        subscribeToForm,
        unsubscribeFromForm,
        subscribeToEntityRecord,
        unsubscribeFromEntityRecord,
        stableUsers,
        assignFormToUser,
        updateFormSubscribers,
    ]);

    return (
        <UserContext.Provider value={userValue}>
            {children}
        </UserContext.Provider>
    );
};